import React, { useCallback, useEffect, useRef, useState } from 'react';

const MAX_MESSAGE_LENGTH = 500;

export default function WhotTextChat({ socket, matchId, playerId, disabled = false, visible = true, bottomPlacement = false }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const listRef = useRef(null);
  const openRef = useRef(false);
  const playerIdRef = useRef(playerId);
  const rootRef = useRef(null);

  useEffect(() => {
    playerIdRef.current = playerId;
  }, [playerId]);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    if (!visible) {
      setOpen(false);
      openRef.current = false;
    }
  }, [visible]);

  useEffect(() => {
    if (!open || !window.visualViewport || !rootRef.current) return undefined;

    const updateViewportOffset = () => {
      const viewport = window.visualViewport;
      const keyboardHeight = Math.max(
        0,
        window.innerHeight - viewport.height - viewport.offsetTop,
      );
      rootRef.current.style.setProperty('--whot-chat-keyboard-offset', keyboardHeight + 'px');
    };

    updateViewportOffset();
    window.visualViewport.addEventListener('resize', updateViewportOffset);
    window.visualViewport.addEventListener('scroll', updateViewportOffset);
    return () => {
      window.visualViewport.removeEventListener('resize', updateViewportOffset);
      window.visualViewport.removeEventListener('scroll', updateViewportOffset);
    };
  }, [open]);

  useEffect(() => {
    setMessages([]);
    setDraft('');
    setUnreadCount(0);
    setOpen(false);
    openRef.current = false;
  }, [matchId]);

  useEffect(() => {
    if (!socket || disabled) return undefined;
    const handleMessage = (data) => {
      if (!data || String(data.matchId || matchId) !== String(matchId)) return;
      if (typeof data.message !== 'string' || !data.message.trim()) return;

      const senderId = data.userId != null ? String(data.userId) : null;
      const currentUserId = playerIdRef.current != null ? String(playerIdRef.current) : null;
      const fromOtherPlayer = senderId != null && currentUserId != null && senderId !== currentUserId;

      setMessages((current) => [...current, {
        userId: data.userId,
        username: typeof data.username === 'string' && data.username.trim() ? data.username.trim() : 'Player',
        message: data.message,
        timestamp: Number(data.timestamp) || Date.now(),
      }]);

      if (fromOtherPlayer && !openRef.current) {
        setUnreadCount((count) => count + 1);
      }
    };
    socket.on('messageReceived', handleMessage);
    return () => socket.off('messageReceived', handleMessage);
  }, [socket, matchId, disabled]);

  useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const toggleOpen = useCallback(() => {
    setOpen((currentOpen) => {
      const nextOpen = !currentOpen;
      openRef.current = nextOpen;
      if (nextOpen) setUnreadCount(0);
      return nextOpen;
    });
  }, []);

  const send = useCallback(() => {
    if (disabled || !socket || !matchId) return;
    const message = draft.trim();
    if (!message || message.length > MAX_MESSAGE_LENGTH) return;
    socket.emit('sendMessage', { matchId, message });
    setDraft('');
  }, [disabled, socket, matchId, draft]);

  const onKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  return (
    <div ref={rootRef} style={styles.root}>
      <button type="button" aria-expanded={open} aria-controls="whot-text-chat-panel"
        aria-label="Text Chat" title="Text Chat"
        onClick={toggleOpen} style={styles.toggle} disabled={!socket}>
        <span aria-hidden="true">💬</span>{unreadCount ? <span style={styles.badge}>+{unreadCount}</span> : null}
      </button>
      {open && (
        <section id="whot-text-chat-panel" aria-label="WHOT match chat" style={{ ...styles.panel, ...(bottomPlacement ? styles.bottomPanel : null) }}>
          <div style={styles.header}>
            <strong>Match Chat</strong>
            <div style={styles.headerRight}>
              <button type="button" aria-label="Close chat" title="Close chat" onClick={toggleOpen} style={styles.close}>×</button>
              <span style={styles.live}>● Live</span>
            </div>
          </div>
          <div ref={listRef} style={styles.messages} role="log" aria-live="polite" aria-label="Chat messages">
            {!messages.length ? <div style={styles.empty}>No messages yet. Say hello 👋</div> :
              messages.map((item, index) => {
                const mine = playerId != null && String(item.userId) === String(playerId);
                return (
                  <div key={`${item.timestamp}-${index}`} style={{ ...styles.row, alignItems: mine ? 'flex-end' : 'flex-start' }}>
                    <span style={styles.username}>{mine ? 'You' : item.username}</span>
                    <div style={{ ...styles.bubble, ...(mine ? styles.mine : styles.other) }}>{item.message}</div>
                  </div>
                );
              })}
          </div>
          <div style={styles.composer}>
            <textarea value={draft} maxLength={MAX_MESSAGE_LENGTH}
              onChange={(event) => setDraft(event.target.value)} onKeyDown={onKeyDown}
              placeholder={disabled ? 'Match ended' : 'Type a message...'} aria-label="Chat message"
              rows={2} disabled={disabled} style={styles.input} />
            <div style={styles.footer}>
              <span style={styles.count}>{draft.length}/{MAX_MESSAGE_LENGTH}</span>
              <button type="button" onClick={send} disabled={disabled || !draft.trim()} style={styles.send}>Send</button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

const styles = {
  root: { position: 'relative', zIndex: 210, '--whot-chat-keyboard-offset': '0px' },
  toggle: { width: 44, height: 44, padding: 0, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: 20, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' },
  badge: { position: 'absolute', top: 1, right: 1, width: 9, height: 9, padding: 0, boxSizing: 'border-box', borderRadius: 999, background: '#f59e0b', border: '2px solid #052e22' },
  panel: { position: 'fixed', left: 'max(8px, env(safe-area-inset-left))', right: 'max(8px, env(safe-area-inset-right))', bottom: 'calc(68px + env(safe-area-inset-bottom) + var(--whot-chat-keyboard-offset))', width: 'auto', height: 'min(40dvh, 320px)', maxHeight: 'min(40dvh, 320px)', minHeight: 0, display: 'flex', flexDirection: 'column', boxSizing: 'border-box', background: 'rgba(10, 18, 32, 0.88)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 14, boxShadow: '0 12px 36px rgba(0,0,0,.38)', overflow: 'hidden' },
  bottomPanel: { top: 'auto' },
  header: { flex: '0 0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '8px 10px', color: '#fff', borderBottom: '1px solid rgba(255,255,255,.08)', minWidth: 0 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  live: { color: '#34d399', fontSize: 10, fontWeight: 800 },
  close: { width: 28, height: 28, padding: 0, border: '1px solid rgba(255,255,255,.14)', borderRadius: 8, background: 'rgba(255,255,255,.07)', color: '#fff', fontSize: 20, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  messages: { flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6, overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' },
  empty: { color: '#9ca3af', fontSize: 12, textAlign: 'center', padding: '24px 10px' },
  row: { display: 'flex', flexDirection: 'column', maxWidth: '92%', gap: 2 },
  username: { color: '#9ca3af', fontSize: 10, fontWeight: 800 },
  bubble: { maxWidth: '100%', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap', padding: '6px 9px', borderRadius: 10, color: '#fff', fontSize: 12, lineHeight: 1.3 },
  mine: { background: 'rgba(6,95,70,.88)', borderBottomRightRadius: 3 },
  other: { background: 'rgba(31,41,55,.88)', borderBottomLeftRadius: 3 },
  composer: { flex: '0 0 auto', padding: '7px 9px calc(7px + env(safe-area-inset-bottom))', borderTop: '1px solid rgba(255,255,255,.08)', background: 'rgba(5,12,22,.56)' },
  input: { width: '100%', boxSizing: 'border-box', resize: 'none', borderRadius: 9, border: '1px solid rgba(255,255,255,.14)', background: 'rgba(11,18,32,.9)', color: '#fff', padding: '7px 8px', outline: 'none', fontSize: 12, lineHeight: 1.3 },
  footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 5 },
  count: { color: '#6b7280', fontSize: 9 },
  send: { border: 0, borderRadius: 8, padding: '7px 12px', background: '#f59e0b', color: '#111', fontWeight: 900, cursor: 'pointer' },
};
